import { FC, useMemo, Fragment, useState } from 'react';
import moment from 'moment';

import { ReactComponent as AddIcon } from 'assets/icons/add_circle_outline.svg';

import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useServiceCustomDays from 'hooks/useServiceCustomDays';
import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';
import { NEXT_23_MONTH } from 'utils/constants';
import EditScheduleModal from '../../EditScheduleModal/EditScheduleModal';
import { IService } from 'interfaces/services';
import ServiceCustomDayItem from './ServiceCustomDayItem/ServiceCustomDayItem';

const DEFAULT_NEWLY_ADDED_CUSTOM_DAY = {
  date: moment().format('YYYY-MM-DD'),
  day: moment().format('dddd'),
  shifts: [
    {
      id: '',
      startTime: '08:00:00',
      endTime: '17:00:00',
      operatories: [],
    },
  ],
  isServiceClosed: false,
};

interface CustomDaysListProps {
  service: IService;
}

const CustomDaysList: FC<CustomDaysListProps> = ({ service }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading } = useServiceCustomDays({
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().add(NEXT_23_MONTH, 'months').format('YYYY-MM-DD'),
    serviceId: service.id,
  });

  const normalizeData = useMemo(() => {
    if (!data) return [];
    const groupedByMonth = data.reduce(
      (acc, curr) => {
        const month = moment(curr.date).format('MMMM YYYY');
        if (!acc[month]) {
          acc[month] = [];
        }
        acc[month].push(curr);
        return acc;
      },
      {} as Record<string, typeof data>
    );

    return Object.entries(groupedByMonth).map(([monthlyTitle, days]) => ({
      monthlyTitle,
      days,
    }));
  }, [data]);

  const isServiceHaveCustomDays = data && data.length > 0;

  const isBlockoutTypeRender =
    document.querySelector('#blockout-type') !== null;

  return (
    <>
      {isModalOpen && (
        <EditScheduleModal
          isCreatedMode
          selectedCellData={DEFAULT_NEWLY_ADDED_CUSTOM_DAY}
          service={service}
          onClose={() => setIsModalOpen(false)}
        />
      )}
      <div className="w-full  border-light-grey">
        {isLoading ? (
          <div className="flex w-full justify-center items-center">
            <LoadingSpinner className="all-child:fill-magenta" />
          </div>
        ) : (
          <div className="relative">
            <button
              className="w-full h-4 flex bg-white justify-center border-b border-light-grey items-center text-magenta gap-x-1 z-0"
              onClick={() => setIsModalOpen(true)}
            >
              <AddIcon className="w-2 h-2 all-child:all-child:fill-magenta" />
              <span className="font-bold">Add custom day</span>
            </button>
            <div
              className={`${isBlockoutTypeRender ? 'h-[calc(100vh-48rem)]' : 'h-[calc(100vh-37.2rem)]'} overflow-y-auto scrollbar pb-1.6`}
            >
              {!isServiceHaveCustomDays ? (
                <div className="flex flex-col justify-center items-center h-full gap-y-2">
                  <EmptyBucket />
                  <p>No custom days to display</p>
                </div>
              ) : (
                normalizeData.map((data) => (
                  <Fragment key={data.monthlyTitle}>
                    <div
                      key={data.monthlyTitle}
                      className="h-4.6 flex items-center px-2 bg-lightest-grey font-bold"
                    >
                      {data.monthlyTitle}
                    </div>
                    {data.days.map((day) => (
                      <ServiceCustomDayItem
                        key={day.date}
                        day={day}
                        service={service}
                      />
                    ))}
                  </Fragment>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomDaysList;
